import * as React from "react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  size?: "sm" | "md";
};

/** Pill toggle: grey off, indigo on, sliding knob.
 *
 *  A native checkbox with role="switch" rather than a <button aria-pressed>:
 *  keyboard handling, form participation and the :checked / :disabled /
 *  :focus-visible states all come free, and the knob hangs off the adjacent
 *  track span so no pseudo-element is asked of the <input> itself.
 *
 *  It renders no visible text — give it an `aria-label`, or point
 *  `aria-labelledby` at the row heading beside it. */
export const Toggle = React.forwardRef<HTMLInputElement, Props>(
  ({ size = "md", className, disabled, id, "aria-describedby": describedBy, ...props }, ref) => {
    const f = useFieldControl({ id, describedBy });


    return (
      <label
        className={cn(
          "vb-toggle",
          size === "sm" && "vb-toggle--sm",
          disabled && "vb-toggle--disabled",
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          className="vb-toggle__input"
          id={f.id}
          aria-describedby={f.describedBy}
          aria-invalid={f.invalid || undefined}
          disabled={disabled}
          {...props}
        />
        <span className="vb-toggle__track" aria-hidden="true" />
      </label>
    );
  },
);
Toggle.displayName = "Toggle";

export default Toggle;
